import {
  CreateGroupRequest,
  CreateGroupResponse,
  SendMessageRequest,
  SendMessageStreamRequest,
  SendMessageResponse,
  LoadHistoryRequest,
  LoadHistoryResponse,
  SearchRequest,
  SearchResponse,
  ApiResponse,
  StreamingEvent,
  SuggestionCard,
} from "../types/api.types";
import { API_URLS } from "../utils/apiConstants";
import {
  handleApiError,
  getErrorType,
  createApiError,
  type ErrorHandlerOptions,
} from "../../../services/errorHandler";
import { backIncstance } from "../../../utils/commonFunctions";

/**
 * TutorApiService
 *
 * AI 튜터 API와의 모든 통신을 담당하는 클래스
 * - 그룹 관리 (생성, 로드)
 * - 메시지 전송 (스트리밍, 일반)
 * - 히스토리 관리
 * - 검색 기능
 *
 * 특징:
 * - 싱글톤 패턴 (필요시 사용)
 * - AbortController를 통한 요청 취소 지원
 * - 자동 에러 처리 및 로깅
 */
export class TutorApiService {
  private baseUrl: string;
  private authToken: string | null = null;
  private abortController: AbortController | null = null;
  private errorHandlerOptions: ErrorHandlerOptions | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || "";
  }

  /**
   * 인증 토큰 설정
   */
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  /**
   * 에러 핸들러 옵션 설정
   */
  setErrorHandlerOptions(options: ErrorHandlerOptions): void {
    this.errorHandlerOptions = options;
  }

  /**
   * 에러 처리 헬퍼
   */
  private handleError(error: any): void {
    if (this.errorHandlerOptions) {
      const errorType = getErrorType(error);
      const apiError = createApiError(
        errorType,
        error.message || "알 수 없는 오류",
        error.detail
      );
      handleApiError(apiError, this.errorHandlerOptions);
    } else {
      console.error("Error handler options not set:", error);
    }
  }

  /**
   * 공통 헤더 생성
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  /**
   * 그룹 생성 (새로운 대화 시작)
   */
  async createGroup(request: CreateGroupRequest): Promise<CreateGroupResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${API_URLS.CREATE_GROUP}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<CreateGroupResponse> = await response.json();

      if (!data.success) {
        throw new Error(data.message || "그룹 생성 실패");
      }

      return data.data;
    } catch (error) {
      console.error("createGroup 에러:", error);
      throw error;
    }
  }

  /**
   * 그룹 히스토리 로드
   */
  async loadGroupHistory(
    request: LoadHistoryRequest
  ): Promise<LoadHistoryResponse> {
    try {
      const params = new URLSearchParams({
        limit: String(request.limit || 50),
        offset: String(request.offset || 0),
      });

      const response = await fetch(
        `${this.baseUrl}${API_URLS.LOAD_HISTORY}/${request.group_id}?${params}`,
        {
          method: "GET",
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<LoadHistoryResponse> = await response.json();

      if (!data.success) {
        throw new Error(data.message || "히스토리 로드 실패");
      }

      return data.data;
    } catch (error) {
      console.error("loadGroupHistory 에러:", error);
      throw error;
    }
  }

  /**
   * 메시지 저장 (비스트리밍)
   */
  async saveMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${API_URLS.SAVE_MESSAGE}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<SendMessageResponse> = await response.json();

      if (!data.success) {
        throw new Error(data.message || "메시지 저장 실패");
      }

      return data.data;
    } catch (error) {
      console.error("saveMessage 에러:", error);
      throw error;
    }
  }

  /**
   * 메시지 전송 (스트리밍)
   *
   * CLOVA-X SSE를 사용하여 실시간 응답을 처리합니다.
   * 레거시 clarioApi.js의 로직을 재현합니다.
   *
   * @param request CLOVA-X 스트리밍 요청 객체
   * @param onChunk 청크 수신 콜백
   * @param onMessageId 메시지 ID 수신 콜백
   * @param onError 에러 처리 콜백
   * @param onComplete 스트리밍 완료 콜백 (최종 데이터 포함)
   */
  async sendMessageStreaming(
    request: SendMessageStreamRequest,
    onChunk: (content: string) => void,
    onMessageId?: (messageId: string) => void,
    onError?: (error: Error) => void,
    onComplete?: (data: any) => void
  ): Promise<"success" | "aborted" | "error"> {
    try {
      // 이전 요청 취소
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      // CLOVA-X API 엔드포인트 사용
      const streamUrl = process.env.REACT_APP_CLARIO_SERVER || "";

      const response = await fetch(streamUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        body: JSON.stringify(request),
        cache: "no-cache",
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("응답 본문이 없습니다");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // 이벤트 경계("\n\n")를 찾아 처리
        let boundaryIndex;
        while ((boundaryIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, boundaryIndex);
          buffer = buffer.slice(boundaryIndex + 2);

          if (!rawEvent.trim()) continue;

          const lines = rawEvent.split("\n");
          let eventType: string | null = null;
          let eventData: string | null = null;

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6).trim();
            }
          }

          try {
            if (!eventData) continue;

            const parsedData = JSON.parse(eventData);

            // ClarioStatus 또는 ClarioResponse 이벤트 처리
            if (
              eventType === "ClarioStatus" ||
              eventType === "ClarioResponse"
            ) {
              // 스트리밍 완료 데이터 (type: "all")
              if (parsedData?.type === "all") {
                onComplete?.(parsedData);
                continue;
              }

              if (
                parsedData?.type === "DONE" ||
                parsedData?.type === "main" ||
                parsedData?.type === "sub" ||
                parsedData?.type === "FINISH"
              ) {
                // 딜레이 추가 (레거시 동작 재현)
                await new Promise((resolve) => setTimeout(resolve, 40));
              }

              if (parsedData?.type === "FINISH") {
                await new Promise((resolve) => setTimeout(resolve, 80));
              }

              // 텍스트 청크 전달 (type: "response"일 때만)
              if (parsedData?.type === "response" && parsedData?.text) {
                // "†" 마커 제거
                let cleanText = parsedData.text.replace(/†/g, "");

                // 테이블 형식 변환: <br>| → \n|
                cleanText = cleanText.replace(/<br>\|/g, "\n|");
                // 테이블 끝부분 변환: |<br><br> → | \n\n
                cleanText = cleanText.replace(/\|<br><br>$/g, "| \n\n");

                onChunk(cleanText);
              }
            }
            // ClarioMessageID 이벤트 처리
            else if (eventType === "ClarioMessageID") {
              if (parsedData?.text) {
                onMessageId?.(parsedData.text);
              }
            }
          } catch (parseError) {
            console.error("❌ JSON parse error:", parseError, eventData);
          }
        }

        // Abort 체크
        if (this.abortController?.signal.aborted) {
          return "aborted";
        }
      }

      return "success";
    } catch (error) {
      console.error("sendMessageStreaming 에러:", error);

      if (error instanceof Error && error.name === "AbortError") {
        return "aborted";
      }

      onError?.(error instanceof Error ? error : new Error("알 수 없는 에러"));
      return "error";
    } finally {
      this.abortController = null;
    }
  }

  /**
   * 스트리밍 요청 취소
   */
  cancelStreaming(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * 검색
   */
  async search(request: SearchRequest): Promise<SearchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${API_URLS.SEARCH}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<SearchResponse> = await response.json();

      if (!data.success) {
        throw new Error(data.message || "검색 실패");
      }

      return data.data;
    } catch (error) {
      console.error("search 에러:", error);
      throw error;
    }
  }

  /**
   * 범용 API 호출 (레거시 touchAPI 마이그레이션)
   *
   * backIncstance를 사용하여 기존 Tutor.js의 touchAPI 동작을 재현합니다.
   * - 401, 403, 404 에러 자동 처리
   * - 쿠키 기반 인증
   *
   * @param method HTTP 메서드
   * @param url API URL (환경변수 사용 권장)
   * @param data 요청 데이터
   * @returns JSON 응답 데이터
   */
  async touchAPI(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    data?: any
  ): Promise<any> {
    try {
      const response = await backIncstance(method, url, data);

      // backIncstance가 Error 객체를 반환한 경우 (catch 블록에서)
      if (response instanceof Error) {
        console.error("backIncstance 에러:", response);
        throw response;
      }

      // Response 객체가 아닌 경우
      if (!response || typeof response !== "object") {
        console.error("잘못된 응답 형식:", response);
        throw new Error("서버 응답 형식이 올바르지 않습니다");
      }

      // 에러 응답 처리
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        this.handleError({
          status: response.status,
          message: response.statusText || "인증 오류",
        });
        throw createApiError(
          response.status,
          response.statusText || "인증이 필요합니다"
        );
      }

      // 503 서비스 불가 에러
      if (response.status === 503) {
        throw createApiError(503, "서비스를 일시적으로 이용할 수 없습니다");
      }

      // 응답이 ok가 아닌 경우 (backIncstance가 이미 Response를 반환)
      if (!response.ok) {
        // backIncstance에서 이미 body를 읽었을 수 있으므로 주의
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
        } catch (e) {
          // body를 읽을 수 없으면 기본 메시지 사용
          console.warn("에러 응답 파싱 실패:", e);
        }

        console.error("API 에러 응답:", {
          status: response.status,
          statusText: response.statusText,
          url: url,
          message: errorMessage,
        });
        throw createApiError(response.status, errorMessage);
      }

      // JSON 파싱
      const resData = await response.json();
      return resData;
    } catch (error) {
      console.error("touchAPI 에러:", error);
      // 에러 재throw
      throw error;
    }
  }

  /**
   * 채팅 저장 + 그룹 연결 (복합 API 호출)
   *
   * 1. 채팅 메시지를 저장 (POST/PATCH)
   * 2. response_id를 받아서 그룹에 연결 (POST)
   *
   * @param method POST (신규) 또는 PATCH (수정)
   * @param chatUrl 채팅 저장 API URL
   * @param chatData 채팅 데이터
   * @param groupId 그룹 ID
   * @returns response_id와 id를 포함한 응답
   */
  async touchAPIWithGroupFollow(
    method: "POST" | "PATCH",
    chatUrl: string,
    chatData: any,
    groupId: number
  ): Promise<{ response_id: string; id: string }> {
    try {
      // 1단계: 채팅 저장
      const addChat = await this.touchAPI(method, chatUrl, chatData);

      // PATCH의 경우 응답에 response_id가 없을 수 있음 → chatData의 response_id를 fallback으로 사용
      // (레거시 Tutor.js:290-292 패턴 참조)
      const responseId = addChat?.response_id || chatData?.response_id;

      if (!responseId) {
        throw new Error("채팅 저장 실패: response_id를 받지 못했습니다");
      }

      // 2단계: 그룹에 연결
      const reqData = {
        group_id: groupId,
        response_id: responseId,
      };

      const addGroup = await this.touchAPI("POST", API_URLS.ADD_GROUP, reqData);

      return {
        response_id: responseId,
        id: addGroup.id || addGroup.response_id,
      };
    } catch (error) {
      console.error("touchAPIWithGroupFollow 에러:", error);
      throw error;
    }
  }

  /**
   * 채팅 히스토리 조회 (레거시 호환)
   *
   * @param mode "tutor" | "socrates"
   * @param groupId 그룹 ID
   * @returns 채팅 히스토리 배열
   */
  async fetchChatHistory(
    mode: string,
    groupId: string
  ): Promise<import("../types/tutor.types").ChatMessage[]> {
    try {
      const url =
        mode === "socrates"
          ? process.env.REACT_APP_SOCRATES_GET_HISTORY_LIST
          : process.env.REACT_APP_TUTOR_GET_HISTORY_LIST;

      if (!url) {
        throw new Error("채팅 히스토리 URL이 설정되지 않았습니다");
      }

      const response = await this.touchAPI("GET", `${url}/${groupId}`);

      if (!response || !response.chat_list) {
        return [];
      }

      return response.chat_list;
    } catch (error) {
      console.error("fetchChatHistory 에러:", error);
      throw error;
    }
  }

  /**
   * 평가(Reaction) 제출
   *
   * @param responseId 응답 ID
   * @param simpleEva 평가 (0: 싫어요, 1: 좋아요)
   * @param eva 피드백 텍스트 (선택)
   */
  async submitReaction(
    responseId: string,
    simpleEva: 0 | 1 | null,
    eva?: string
  ): Promise<void> {
    try {
      const url = process.env.REACT_APP_TUTOR_EVALUATION;

      if (!url) {
        throw new Error("평가 API URL이 설정되지 않았습니다");
      }

      await this.touchAPI("PUT", url, {
        response_id: responseId,
        simple_eva: simpleEva,
        eva: eva || "",
      });
    } catch (error) {
      console.error("submitReaction 에러:", error);
      throw error;
    }
  }

  /**
   * 파일 업로드 (문서 텍스트 추출)
   *
   * 지원 형식: hwp(x), doc(x), pdf, xlsx, txt
   *
   * @param file 업로드할 파일
   * @returns 추출된 텍스트
   */
  async uploadFile(file: File): Promise<{ result: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const url =
        process.env.REACT_APP_TUTOR_UPLOAD ||
        process.env.REACT_APP_SOCRATES_UPLOAD ||
        "";

      if (!url) {
        throw new Error("파일 업로드 URL이 설정되지 않았습니다");
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `파일 업로드 실패: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // 에러 응답 체크 (unsupported format 등)
      if (data.detail) {
        throw new Error("지원하지 않는 파일 형식입니다");
      }

      return data;
    } catch (error) {
      console.error("uploadFile 에러:", error);
      throw error;
    }
  }

  /**
   * Suggestion Cards 조회 (레거시 호환)
   *
   * Empty State UI에 표시할 제안 카드를 가져옵니다.
   *
   * @param mode "tutor" | "socrates"
   * @returns Suggestion Cards 배열
   */
  async fetchSuggestions(
    mode: "tutor" | "socrates"
  ): Promise<SuggestionCard[]> {
    try {
      const url = process.env.REACT_APP_GREETING_MSG;

      if (!url) {
        throw new Error("Greeting Message URL이 설정되지 않았습니다");
      }

      const kind = mode === "socrates" ? "socra" : "tutor";
      const response = await this.touchAPI("GET", `${url}?kind=${kind}`);

      // API 응답 구조에 맞게 파싱
      // 레거시 응답 형식: { suggestions: [...] } 또는 직접 배열
      if (Array.isArray(response)) {
        return response;
      }

      if (response?.suggestions && Array.isArray(response.suggestions)) {
        return response.suggestions;
      }

      // 응답 형식이 예상과 다른 경우 빈 배열 반환
      console.warn("예상하지 못한 suggestion 응답 형식:", response);
      return [];
    } catch (error) {
      console.error("fetchSuggestions 에러:", error);
      // 에러 발생 시 빈 배열 반환 (fallback은 hook에서 처리)
      throw error;
    }
  }

  /**
   * 스트리밍 이벤트 파싱
   */
  private parseStreamingEvent(rawEvent: string): StreamingEvent {
    const lines = rawEvent.split("\n");
    const event: Partial<StreamingEvent> = {};

    for (const line of lines) {
      if (line.startsWith("event:")) {
        event.type = line.slice(6).trim() as any;
      } else if (line.startsWith("data:")) {
        const dataStr = line.slice(5).trim();
        try {
          event.data = JSON.parse(dataStr);
        } catch {
          event.data = dataStr;
        }
      }
    }

    event.timestamp = new Date().toISOString();

    return event as StreamingEvent;
  }
}

// 싱글톤 인스턴스
export const tutorApiService = new TutorApiService();
